import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSettings, setSettings } from '../utils/storage';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Default settings structure
const defaultSettings = {
  voiceURI: '',
  rate: 1,
  pitch: 1,
  volume: 1,
  email: '',
  subscription: false,
  chatgptApiKey: '',
  anchorText: '',
};

// Migrate old localStorage keys to new centralized storage
const migrateOldSettings = () => {
  const oldVoiceURI = localStorage.getItem('speechVoiceURI');
  const oldRate = localStorage.getItem('speechRate');
  const oldPitch = localStorage.getItem('speechPitch');
  const oldVolume = localStorage.getItem('speechVolume');
  
  if (oldVoiceURI || oldRate || oldPitch || oldVolume) {
    const migrated = {
      ...defaultSettings,
      ...(oldVoiceURI && { voiceURI: oldVoiceURI }),
      ...(oldRate && { rate: parseFloat(oldRate) }),
      ...(oldPitch && { pitch: parseFloat(oldPitch) }),
      ...(oldVolume && { volume: parseFloat(oldVolume) }),
    };
    setSettings(migrated);
    
    // Clean up old keys
    localStorage.removeItem('speechVoiceURI');
    localStorage.removeItem('speechRate');
    localStorage.removeItem('speechPitch');
    localStorage.removeItem('speechVolume');
    
    return migrated;
  }
  return null;
};

export const SettingsProvider = ({ children }) => {
  // Initialize settings from localStorage or use defaults
  const [settings, setSettingsState] = useState(() => {
    const saved = getSettings();
    if (saved) {
      return { ...defaultSettings, ...saved };
    }
    
    // Try to migrate old settings
    const migrated = migrateOldSettings();
    if (migrated) {
      return migrated;
    }
    
    return defaultSettings;
  });

  // Save settings to localStorage whenever they change
  useEffect(() => {
    setSettings(settings);
  }, [settings]);

  const updateVoiceURI = (newVoiceURI) => {
    setSettingsState(prev => ({ ...prev, voiceURI: newVoiceURI }));
  };

  const updateRate = (newRate) => {
    setSettingsState(prev => ({ ...prev, rate: newRate }));
  };

  const updatePitch = (newPitch) => {
    setSettingsState(prev => ({ ...prev, pitch: newPitch }));
  };

  const updateVolume = (newVolume) => {
    setSettingsState(prev => ({ ...prev, volume: newVolume }));
  };

  const updateEmail = (newEmail) => {
    setSettingsState(prev => ({ ...prev, email: newEmail }));
  };

  const updateSubscription = (newSubscription) => {
    setSettingsState(prev => ({ ...prev, subscription: newSubscription }));
  };

  const updateChatGPTApiKey = (newApiKey) => {
    setSettingsState(prev => ({ ...prev, chatgptApiKey: newApiKey }));
  };

  const updateAnchorText = (newAnchorText) => {
    setSettingsState(prev => ({ ...prev, anchorText: newAnchorText }));
  };

  return (
    <SettingsContext.Provider
      value={{
        voiceURI: settings.voiceURI,
        rate: settings.rate,
        pitch: settings.pitch,
        volume: settings.volume,
        email: settings.email,
        subscription: settings.subscription,
        chatgptApiKey: settings.chatgptApiKey,
        anchorText: settings.anchorText,
        updateVoiceURI,
        updateRate,
        updatePitch,
        updateVolume,
        updateEmail,
        updateSubscription,
        updateChatGPTApiKey,
        updateAnchorText,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
